import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
// Copyright 1999-2020. Plesk International GmbH. All rights reserved.
import React, { isValidElement, cloneElement, Children } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { CLS_PREFIX } from '../../constants';
import Icon, { ICON_SIZE_12, ICON_SIZE_16, ICON_SIZE_24, ICON_SIZE_32, ICON_SIZE_48, ICON_SIZE_64, ICON_SIZE_96, ICON_SIZE_128, ICON_SIZE_192 } from '../Icon';
import Heading from '../Heading';

/**
 * `Media` component is a combination of icon, text title and content.
 * This layout was so widely spread in our products so we have decided to create a special component for it.
 * Probably we should have named the component differently (not Media), but we didn't have better idea then ;)
 * @since 0.0.45
 */
var Media = function Media(_ref) {
  var baseClassName = _ref.baseClassName,
      className = _ref.className,
      children = _ref.children,
      title = _ref.title,
      titleAddon = _ref.titleAddon,
      titleSize = _ref.titleSize,
      image = _ref.image,
      imageSpacing = _ref.imageSpacing,
      props = _objectWithoutProperties(_ref, ["baseClassName", "className", "children", "title", "titleAddon", "titleSize", "image", "imageSpacing"]);

  if (!imageSpacing && isValidElement(image) && image.type === Icon) {
    var _map;

    var map = (_map = {}, _defineProperty(_map, ICON_SIZE_12, 'sm'), _defineProperty(_map, ICON_SIZE_16, 'sm'), _defineProperty(_map, ICON_SIZE_24, 'md'), _defineProperty(_map, ICON_SIZE_32, 'md'), _defineProperty(_map, ICON_SIZE_48, 'lg'), _defineProperty(_map, ICON_SIZE_64, 'xl'), _defineProperty(_map, ICON_SIZE_96, 'xxl'), _defineProperty(_map, ICON_SIZE_128, 'xxl'), _defineProperty(_map, ICON_SIZE_192, 'xxl'), _map);
    imageSpacing = map[image.props.size];
  }

  var renderTitle = function renderTitle(title) {
    if (!title) {
      return null;
    }

    if (typeof title === 'string') {
      return React.createElement(Heading, {
        level: 5,
        className: "".concat(baseClassName, "__title")
      }, title);
    }

    if (isValidElement(title)) {
      return cloneElement(title, {
        className: classNames("".concat(baseClassName, "__title"), title.props.className)
      });
    }

    return null;
  };

  return React.createElement("div", _extends({
    className: classNames(baseClassName, className)
  }, props), image && React.createElement("figure", {
    className: classNames("".concat(baseClassName, "__figure"), _defineProperty({}, "".concat(baseClassName, "__figure--").concat(imageSpacing), imageSpacing))
  }, image), React.createElement("div", {
    className: "".concat(baseClassName, "__body")
  }, title || titleAddon ? React.createElement("div", {
    className: classNames("".concat(baseClassName, "__header"), _defineProperty({}, "".concat(baseClassName, "__header--").concat(titleSize), titleSize))
  }, renderTitle(title), titleAddon && React.createElement("div", {
    className: "".concat(baseClassName, "__title-addon")
  }, titleAddon)) : null, Children.toArray(children).length ? React.createElement("div", {
    className: "".concat(baseClassName, "__content")
  }, children) : null));
};

Media.propTypes = {
  /**
   * Title
   * @since 0.0.45
   */
  title: PropTypes.node,

  /**
   * Title addon
   * @since 0.0.68
   */
  titleAddon: PropTypes.node,

  /**
   * Title size
   * @deprecated Pass a component as a title prop
   * @since 0.0.68
   */
  titleSize: PropTypes.oneOf(['md', 'lg', 'xl']),

  /**
   * Image
   * @since 0.0.45
   */
  image: PropTypes.any,

  /**
   * Spacings between image and content
   * @since 0.3.0
   */
  imageSpacing: PropTypes.oneOf(['sm', 'md', 'lg', 'xl', 'xxl']),

  /**
   * Content of the `Media`.
   * @since 0.0.45
   */
  children: PropTypes.node,

  /**
   * @ignore
   */
  className: PropTypes.string,

  /**
   * @ignore
   */
  baseClassName: PropTypes.string
};
Media.defaultProps = {
  title: null,
  titleAddon: null,
  titleSize: null,
  image: null,
  imageSpacing: null,
  children: null,
  className: null,
  baseClassName: "".concat(CLS_PREFIX, "media")
};
export default Media;